// Full split-screen tech review.
// Left: source document (PDF/image via SAS URL).
// Right: scrollable form bound to GET /api/rx/{id}; saves via PATCH; actions hit
// /api/rx/{id}/{hold|import|reject}.

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { apiGet, apiPost } from "../lib/api";
import { ConfidenceField } from "../components/ConfidenceField";
import { MatchBanner } from "../components/MatchBanner";
import { HoldDialog } from "../components/HoldDialog";
import { RejectDialog } from "../components/RejectDialog";
import { FormulaPicker } from "../components/FormulaPicker";
import { PatientPicker } from "../components/PatientPicker";
import { PrescriberPicker } from "../components/PrescriberPicker";
import type { RxDetail, HoldReason } from "@shared/types";
import { VaccineReview } from "./VaccineReview";

// Therapy types — mirrors db/schema.sql seed of dbo.therapy_type. Kept inline
// to avoid a roundtrip; can be replaced by GET /api/therapy-types later.
const THERAPY_TYPES = [
  "ADULT SUSPENSIONS","ALLERGY","ANTI-INFECTIVE","ANXIETY/MOOD","AUTISM","BHRT FEMALE","BHRT MALE",
  "CHELATION","CLINICAL TRIAL","COLORECTAL","DENTAL","DERMATOLOGY","EAR, NOSE & THROAT",
  "ERECTILE DYSFUNCTION","FEE","GASTROINTESTINAL","HOSPICE","IDEAL IMAGE","INHALATION","INJECTABLES",
  "IRRIGATION","LOW DOSE NALTREXONE","METOBOLIC CODE","MISCELLANEOUS","NON-COMPOUNDS","OB/GYN",
  "ONCOLOGY","OPHTHALMICS","OTC","PAIN MANAGEMNET","PEDIATRIC","PODIATRY","PROCTOLOGY","SHIPPING",
  "STERILE","SUPPLIES","THYROID","UROLOGY","VETERINARY","VITAMINS",
  "WEIGHT MANAGEMENT-INJ","WEIGHT MANAGEMENT-ORAL","WOUND CARE",
];

// Maps RxDetail field paths the user can edit → the snake_case DB column the PATCH endpoint expects.
// When a field is edited we add the col name to `dirty` and read the current value out of `rx`.
type Dirty = Set<string>;

function setIn<T extends object>(obj: T, path: string[], value: unknown): T {
  if (path.length === 0) return obj;
  const next: any = Array.isArray(obj) ? [...obj] : { ...obj };
  let cur = next;
  for (let i = 0; i < path.length - 1; i++) {
    const k = path[i]!;
    cur[k] = Array.isArray(cur[k]) ? [...cur[k]] : { ...cur[k] };
    cur = cur[k];
  }
  cur[path[path.length - 1]!] = value;
  return next;
}

function isPdf(filename: string | null): boolean {
  return !!filename && /\.pdf$/i.test(filename);
}

export function Review() {
  const { rxId } = useParams();
  const navigate = useNavigate();
  const [rx, setRx] = useState<RxDetail | null>(null);
  const [dirty, setDirty] = useState<Dirty>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showHold, setShowHold] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [showFormulaPicker, setShowFormulaPicker] = useState(false);
  const [showPatientPicker, setShowPatientPicker] = useState(false);
  const [showPrescriberPicker, setShowPrescriberPicker] = useState(false);

  function reload() {
    if (!rxId) return;
    apiGet<RxDetail>(`/rx/${rxId}`)
      .then((data) => { setRx(data); setError(null); })
      .catch((e) => setError(String(e)));
  }

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    apiGet<RxDetail>(`/rx/${rxId}`, ctrl.signal)
      .then((data) => { setRx(data); setError(null); })
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
    return () => ctrl.abort();
  }, [rxId]);

  function update(path: string[], col: string, value: unknown) {
    if (!rx) return;
    setRx(setIn(rx, path, value));
    setDirty((d) => new Set(d).add(col));
  }

  async function save(): Promise<boolean> {
    if (!rx || dirty.size === 0) return true;
    setSaving(true);
    try {
      const body = buildPatchBody(rx, dirty);
      const res = await fetch(`/api/rx/${rx.rxId}`, {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`PATCH → ${res.status}`);
      setDirty(new Set());
      return true;
    } catch (e) {
      setError(String(e));
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function doHold(reason: HoldReason, note: string) {
    if (!rx) return;
    if (dirty.size > 0) await save();
    await apiPost(`/rx/${rx.rxId}/hold`, { reason, note });
    setShowHold(false);
    navigate("/");
  }

  async function doReject(reason: string) {
    if (!rx) return;
    await apiPost(`/rx/${rx.rxId}/reject`, { reason });
    setShowReject(false);
    navigate("/");
  }

  async function doImport() {
    if (!rx) return;
    const ok = await save();
    if (!ok) return;
    await apiPost(`/rx/${rx.rxId}/import`, { method: "pad_bridge" });
    navigate("/");
  }

  if (loading) {
    return <div className="p-6 text-ink-soft text-sm">Loading Rx…</div>;
  }
  if (error || !rx) {
    return (
      <div className="p-6">
        <Link to="/" className="text-accent text-sm hover:underline">← Back</Link>
        <p className="text-bad mt-4">Couldn't load this Rx. {error}</p>
      </div>
    );
  }

  // Vaccine records get their own review UI (no compound/formula/prescriber matching).
  if (rx.recordType === "vaccine") {
    return <VaccineReview rx={rx} onRefetch={reload} />;
  }

  return (
    <div className="flex flex-col h-full">
      <Toolbar rx={rx} />
      <div className="grid grid-cols-[1fr_560px] flex-1 overflow-hidden">
        <DocumentPanel rx={rx} />
        <FormPanel
          rx={rx}
          update={update}
          onOpenFormulaPicker={() => setShowFormulaPicker(true)}
          onOpenPatientPicker={() => setShowPatientPicker(true)}
          onOpenPrescriberPicker={() => setShowPrescriberPicker(true)}
        />
      </div>
      <ActionBar
        rx={rx}
        dirty={dirty.size}
        saving={saving}
        onSave={save}
        onHold={() => setShowHold(true)}
        onReject={() => setShowReject(true)}
        onImport={doImport}
      />
      {showHold && <HoldDialog onConfirm={doHold} onCancel={() => setShowHold(false)} />}
      {showReject && <RejectDialog onConfirm={doReject} onCancel={() => setShowReject(false)} />}
      {showFormulaPicker && (
        <FormulaPicker
          rxId={rx.rxId}
          currentFormulaId={rx.formula.matched?.formulaId ?? null}
          onPicked={() => { setShowFormulaPicker(false); reload(); }}
          onCancel={() => setShowFormulaPicker(false)}
        />
      )}
      {showPatientPicker && (
        <PatientPicker
          rxId={rx.rxId}
          storeCode={rx.store.code}
          currentPkId={rx.patient.matched?.pkId ?? null}
          onPicked={() => { setShowPatientPicker(false); reload(); }}
          onCancel={() => setShowPatientPicker(false)}
        />
      )}
      {showPrescriberPicker && (
        <PrescriberPicker
          rxId={rx.rxId}
          storeCode={rx.store.code}
          currentPkId={rx.prescriber.matched?.pkId ?? null}
          onPicked={() => { setShowPrescriberPicker(false); reload(); }}
          onCancel={() => setShowPrescriberPicker(false)}
        />
      )}
    </div>
  );
}

function Toolbar({ rx }: { rx: RxDetail }) {
  const navigate = useNavigate();
  const idx = rx.siblingRxIds.indexOf(rx.rxId);
  const prevId = idx > 0 ? rx.siblingRxIds[idx - 1] : null;
  const nextId = idx >= 0 && idx < rx.siblingRxIds.length - 1 ? rx.siblingRxIds[idx + 1] : null;
  return (
    <div className="flex items-center gap-3 px-4 h-11 bg-surface border-b border-border text-sm">
      <Link to="/" className="text-accent hover:underline">← Queue</Link>
      <span className="text-ink-soft">Intake #{rx.intakeId}</span>
      <span className="px-2 py-0.5 rounded-full bg-pill text-xs">{rx.intake.source}</span>
      {rx.intake.pageCount && <span className="px-2 py-0.5 rounded-full bg-pill text-xs">{rx.intake.pageCount} page{rx.intake.pageCount === 1 ? "" : "s"}</span>}
      <span className="px-2 py-0.5 rounded-full bg-accent-soft text-accent text-xs">{rx.intake.documentType ?? "—"}</span>
      <div className="flex-1" />
      <div className="flex items-center gap-1 px-2 py-1 rounded bg-accent-soft text-accent font-semibold text-xs">
        <button
          type="button"
          disabled={prevId === null}
          onClick={() => prevId !== null && navigate(`/review/${prevId}`)}
          className="px-1.5 disabled:opacity-30"
          title="Previous Rx in document"
        >‹</button>
        <span>Rx {rx.indexInDoc + 1} of {rx.totalRxInDoc}</span>
        <button
          type="button"
          disabled={nextId === null}
          onClick={() => nextId !== null && navigate(`/review/${nextId}`)}
          className="px-1.5 disabled:opacity-30"
          title="Next Rx in document"
        >›</button>
      </div>
    </div>
  );
}

function DocumentPanel({ rx }: { rx: RxDetail }) {
  const url = rx.intake.documentUrl;
  const pdf = isPdf(rx.intake.originalFilename);
  return (
    <section className="bg-[#2a3142] p-3 overflow-auto flex flex-col gap-2">
      <div className="text-white/80 text-xs flex items-center gap-2">
        <span className="opacity-80">{rx.intake.originalFilename ?? "Source document"}</span>
      </div>
      <div className="flex-1 bg-white rounded overflow-hidden shadow-lg">
        {!url ? (
          <div className="h-full flex items-center justify-center text-ink-soft text-sm">No document URL.</div>
        ) : pdf ? (
          <iframe title="Source document" src={url} className="w-full h-full border-0" />
        ) : (
          <img alt="Source document" src={url} className="w-full h-full object-contain" />
        )}
      </div>
    </section>
  );
}

function FormPanel({ rx, update, onOpenFormulaPicker, onOpenPatientPicker, onOpenPrescriberPicker }: {
  rx: RxDetail;
  update: (path: string[], col: string, val: unknown) => void;
  onOpenFormulaPicker: () => void;
  onOpenPatientPicker: () => void;
  onOpenPrescriberPicker: () => void;
}) {
  const patientMatchKind = rx.patient.matched ? "matched" : rx.patient.nameRaw ? "partial" : "none";
  const prescriberMatchKind = rx.prescriber.matched ? "matched" : rx.prescriber.nameRaw ? "partial" : "none";
  const formulaConf = rx.formula.matchConfidence;
  const formulaMatchKind: "matched" | "partial" | "none" =
    rx.formula.matched && (formulaConf ?? 0) >= 0.85 ? "matched"
    : rx.formula.matched                              ? "partial"
    :                                                   "none";

  return (
    <aside className="bg-surface border-l border-border overflow-auto">
      {rx.warnings.length > 0 && (
        <div className="bg-warn-soft border-l-4 border-warn px-4 py-3">
          <h3 className="text-xs uppercase tracking-wide text-warn font-semibold mb-1">
            {rx.warnings.length} warning{rx.warnings.length === 1 ? "" : "s"} on this Rx
          </h3>
          <ul className="pl-4 list-disc text-sm">
            {rx.warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}

      <section className="px-4 py-3 border-b border-border">
        <h3 className="text-xs uppercase tracking-wide text-ink-soft mb-2">Patient</h3>
        <MatchBanner
          kind={patientMatchKind}
          title={rx.patient.matched
            ? `${rx.patient.matched.firstName} ${rx.patient.matched.lastName}`
            : rx.patient.nameRaw ?? "(no name extracted)"}
          detail={rx.patient.matched
            ? `Patient ID #${rx.patient.matched.pkId} · DOB ${rx.patient.matched.dob ?? "—"}`
            : rx.patient.nameRaw ? "No PK record linked yet" : undefined}
          onSearch={onOpenPatientPicker}
        />
        <ConfidenceField label="Name"  value={rx.patient.nameRaw}    onChange={(v) => update(["patient", "nameRaw"], "patient_name_raw", v)} />
        <ConfidenceField label="DOB"   value={rx.patient.dobRaw}     type="date" onChange={(v) => update(["patient", "dobRaw"], "patient_dob_raw", v)} />
        <ConfidenceField label="Phone" value={rx.patient.phoneRaw}   onChange={(v) => update(["patient", "phoneRaw"], "patient_phone_raw", v)} />
        <ConfidenceField label="Address" value={rx.patient.addressRaw} onChange={(v) => update(["patient", "addressRaw"], "patient_address_raw", v)} />
        <ConfidenceField label="Species">
          <select
            value={rx.patient.species}
            onChange={(e) => update(["patient", "species"], "patient_species", e.target.value)}
            className="w-full px-2.5 py-1.5 border border-border rounded text-sm focus:outline-none focus:ring-2 focus:ring-accent"
          >
            {["human","canine","feline","equine","avian","other"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </ConfidenceField>
        {rx.patient.species !== "human" && (
          <ConfidenceField label="Pet name" value={rx.patient.petName} onChange={(v) => update(["patient", "petName"], "patient_pet_name", v)} />
        )}
      </section>

      <section className="px-4 py-3 border-b border-border">
        <h3 className="text-xs uppercase tracking-wide text-ink-soft mb-2">Prescriber</h3>
        <MatchBanner
          kind={prescriberMatchKind}
          title={rx.prescriber.matched
            ? `${rx.prescriber.matched.firstName ?? ""} ${rx.prescriber.matched.lastName}`.trim()
            : rx.prescriber.nameRaw ?? "(no name extracted)"}
          detail={rx.prescriber.matched
            ? `NPI ${rx.prescriber.matched.npi ?? "—"}${rx.prescriber.matched.cannotWriteControlled ? " · cannot write controlled" : ""}`
            : "No PK prescriber linked yet"}
          onSearch={onOpenPrescriberPicker}
        />
        <ConfidenceField label="Name"  value={rx.prescriber.nameRaw} onChange={(v) => update(["prescriber", "nameRaw"], "prescriber_name_raw", v)} />
        <ConfidenceField label="NPI"   value={rx.prescriber.npiRaw}  onChange={(v) => update(["prescriber", "npiRaw"], "prescriber_npi_raw", v)} />
        <ConfidenceField label="DEA"   value={rx.prescriber.deaRaw}  onChange={(v) => update(["prescriber", "deaRaw"], "prescriber_dea_raw", v)} />
        <ConfidenceField label="Phone" value={rx.prescriber.phoneRaw} onChange={(v) => update(["prescriber", "phoneRaw"], "prescriber_phone_raw", v)} />
        <ConfidenceField label="Fax"   value={rx.prescriber.faxRaw}   onChange={(v) => update(["prescriber", "faxRaw"], "prescriber_fax_raw", v)} />
        <ConfidenceField label="Clinic" value={rx.prescriber.clinicRaw} onChange={(v) => update(["prescriber", "clinicRaw"], "prescriber_clinic_raw", v)} />
      </section>

      <section className="px-4 py-3 border-b border-border">
        <h3 className="text-xs uppercase tracking-wide text-ink-soft mb-2 flex items-center gap-2">
          Prescribed
          {rx.controlled.isControlled && (
            <span className="bg-warn-soft text-warn text-[11px] px-1.5 py-0.5 rounded">Schedule {rx.controlled.schedule ?? "?"}</span>
          )}
          {rx.compound.isCompound && (
            <span className="bg-accent-soft text-accent text-[11px] px-1.5 py-0.5 rounded ml-auto">PCCA CPM</span>
          )}
        </h3>
        <ConfidenceField label="Drug"            value={rx.prescription.drug}             onChange={(v) => update(["prescription", "drug"], "drug", v)} />
        <ConfidenceField label="Gen. name"       value={rx.prescription.genericName}      onChange={(v) => update(["prescription", "genericName"], "generic_name", v)} />
        <ConfidenceField label="Form"            value={rx.prescription.form}             onChange={(v) => update(["prescription", "form"], "form", v)} />
        <ConfidenceField label="Sig"             value={rx.prescription.sig} multiline    onChange={(v) => update(["prescription", "sig"], "sig", v)} />
        <ConfidenceField label="Quantity"        value={rx.prescription.quantity}         onChange={(v) => update(["prescription", "quantity"], "quantity", v)} />
        <ConfidenceField label="Days supply"     value={rx.prescription.daysSupply}       type="number" onChange={(v) => update(["prescription", "daysSupply"], "days_supply", v ? Number(v) : null)} />
        <ConfidenceField label="Refills"         value={rx.prescription.refills}          type="number" onChange={(v) => update(["prescription", "refills"], "refills", v ? Number(v) : null)} />
        <ConfidenceField label="Qty on refills"  value={rx.prescription.qtyOnRefills}     onChange={(v) => update(["prescription", "qtyOnRefills"], "qty_on_refills", v)} />
        <ConfidenceField label="Refill interval" value={rx.prescription.refillIntervalDays} type="number" onChange={(v) => update(["prescription", "refillIntervalDays"], "refill_interval_days", v ? Number(v) : null)} />
        <ConfidenceField label="DAW">
          <select
            value={rx.prescription.dawCode ?? ""}
            onChange={(e) => update(["prescription", "dawCode"], "daw_code", e.target.value === "" ? null : Number(e.target.value))}
            className="w-full px-2.5 py-1.5 border border-border rounded text-sm focus:outline-none focus:ring-2 focus:ring-accent"
          >
            <option value="">—</option>
            <option value="0">0 — No DAW</option>
            <option value="1">1 — Substitution not allowed (prescriber)</option>
            <option value="2">2 — Substitution allowed (patient requested)</option>
            <option value="3">3 — Substitution allowed (pharmacist selected)</option>
            <option value="4">4 — Substitution allowed (generic OOS)</option>
            <option value="5">5 — Substitution allowed (brand as generic)</option>
          </select>
        </ConfidenceField>
        <ConfidenceField label="Rx origin">
          <select
            value={rx.prescription.rxOrigin}
            onChange={(e) => update(["prescription", "rxOrigin"], "rx_origin", Number(e.target.value))}
            className="w-full px-2.5 py-1.5 border border-border rounded text-sm focus:outline-none focus:ring-2 focus:ring-accent"
          >
            <option value="0">0 — Not specified</option>
            <option value="1">1 — Written</option>
            <option value="2">2 — Telephone</option>
            <option value="3">3 — Electronic</option>
            <option value="4">4 — Facsimile</option>
            <option value="5">5 — Pharmacy</option>
          </select>
        </ConfidenceField>
        <ConfidenceField label="Date written"  value={rx.prescription.dateWritten}  type="date" onChange={(v) => update(["prescription", "dateWritten"], "date_written", v)} />
        <ConfidenceField label="Rx expiration" value={rx.prescription.rxExpiration} type="date" onChange={(v) => update(["prescription", "rxExpiration"], "rx_expiration", v)} />
        <ConfidenceField label="Counselling">
          <label className="flex items-center gap-2 text-sm pt-1">
            <input
              type="checkbox"
              checked={rx.prescription.counsellingReceived}
              onChange={(e) => update(["prescription", "counsellingReceived"], "counselling_received", e.target.checked)}
            />
            Patient received counselling
          </label>
        </ConfidenceField>
        <ConfidenceField label="Rx note"          value={rx.prescription.rxNote}         multiline onChange={(v) => update(["prescription", "rxNote"], "rx_note", v)} />
        <ConfidenceField label="Dispensing note"  value={rx.prescription.dispensingNote} multiline onChange={(v) => update(["prescription", "dispensingNote"], "dispensing_note", v)} />
      </section>

      <section className="px-4 py-3 border-b border-border">
        <h3 className="text-xs uppercase tracking-wide text-ink-soft mb-2 flex items-center gap-2">
          PK formula
          {rx.formula.matchConfidence !== null && (
            <span className="text-[10px] font-normal normal-case bg-pill text-ink-soft px-1.5 py-0.5 rounded">
              auto-matched · {rx.formula.matchConfidence.toFixed(2)}
            </span>
          )}
        </h3>
        <MatchBanner
          kind={formulaMatchKind}
          title={rx.formula.matched
            ? rx.formula.matched.name
            : "(no PK formula linked)"}
          detail={rx.formula.matched
            ? [
                rx.formula.matched.drugType === "C" ? "Compound" : rx.formula.matched.drugType === "B" ? "Brand" : "Generic",
                rx.formula.matched.form ?? null,
                rx.formula.matched.strength ?? null,
                rx.formula.speedCode ? `SpeedCode ${rx.formula.speedCode}` : null,
                `#${rx.formula.matched.formulaId}`,
              ].filter(Boolean).join(" · ")
            : "Pick from the store's Drug/Formula library so the import lands on the right recipe in PK"}
          onSearch={onOpenFormulaPicker}
        />
      </section>

      <section className="px-4 py-3 border-b border-border">
        <h3 className="text-xs uppercase tracking-wide text-ink-soft mb-2">Therapy type <span className="ml-auto text-[10px] normal-case font-normal bg-pill text-ink-soft px-1.5 py-0.5 rounded">PK "Ship method"</span></h3>
        <ConfidenceField label="Category">
          <select
            value={rx.prescription.shippingMethod ?? ""}
            onChange={(e) => update(["prescription", "shippingMethod"], "shipping_method", e.target.value || null)}
            className="w-full px-2.5 py-1.5 border border-border rounded text-sm focus:outline-none focus:ring-2 focus:ring-accent"
          >
            <option value="">— select —</option>
            {THERAPY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </ConfidenceField>
      </section>

      <section className="px-4 py-3 border-b border-border">
        <h3 className="text-xs uppercase tracking-wide text-ink-soft mb-2">Insurance / billing <span className="ml-auto text-[10px] normal-case font-normal bg-pill text-ink-soft px-1.5 py-0.5 rounded">From patient record</span></h3>
        <ConfidenceField label="Billing NDC" value={rx.prescription.billingNdc} onChange={(v) => update(["prescription", "billingNdc"], "billing_ndc", v)} />
      </section>

      {rx.compound.isCompound && (
        <section className="px-4 py-3 border-b border-border">
          <h3 className="text-xs uppercase tracking-wide text-ink-soft mb-2">
            Compound{rx.compound.base ? ` — ${rx.compound.base} base` : ""}
          </h3>
          <div className="grid grid-cols-[1fr_90px_80px] gap-1.5 mb-2">
            <span className="text-[11px] text-ink-soft uppercase tracking-wide">Ingredient</span>
            <span className="text-[11px] text-ink-soft uppercase tracking-wide">Strength</span>
            <span className="text-[11px] text-ink-soft uppercase tracking-wide">Schedule</span>
            {rx.compound.ingredients.map((ing) => (
              <FragmentRow key={ing.position} name={ing.name} strength={ing.strengthOrPercent} ctrl={ing.isControlled} />
            ))}
          </div>
          <ConfidenceField label="Base"  value={rx.compound.base}  onChange={(v) => update(["compound", "base"], "compound_base", v)} />
          <ConfidenceField label="Notes" value={rx.compound.notes} multiline onChange={(v) => update(["compound", "notes"], "compound_notes", v)} />
        </section>
      )}
    </aside>
  );
}

function FragmentRow({ name, strength, ctrl }: { name: string; strength: string | null; ctrl: boolean }) {
  return (
    <>
      <input defaultValue={name}     className="px-2 py-1 border border-border rounded text-sm" />
      <input defaultValue={strength ?? ""} className="px-2 py-1 border border-border rounded text-sm" />
      <input defaultValue={ctrl ? "✓" : "—"} className="px-2 py-1 border border-border rounded text-sm text-center" />
    </>
  );
}

function ActionBar({
  rx, dirty, saving, onSave, onHold, onReject, onImport,
}: {
  rx: RxDetail; dirty: number; saving: boolean;
  onSave: () => void; onHold: () => void; onReject: () => void; onImport: () => void;
}) {
  const missing: string[] = [];
  if (!rx.patient.matched)    missing.push("patient");
  if (!rx.prescriber.matched) missing.push("prescriber");
  if (!rx.formula.matched)    missing.push("formula");
  const canSend = missing.length === 0;
  const sendTitle = canSend
    ? "Queue for the PAD bot to file in PK"
    : `Cannot send — link ${missing.join(", ")} first`;

  return (
    <footer className="flex items-center gap-2 px-4 py-2.5 border-t border-border bg-surface">
      <span className="px-2.5 py-1 bg-pill rounded text-xs">
        Status: <strong>{rx.status.replace(/_/g, " ")}</strong>
      </span>
      {rx.controlled.isControlled && (
        <span className="px-2.5 py-1 bg-warn-soft text-warn rounded text-xs">Controlled substance</span>
      )}
      {dirty > 0 && (
        <span className="px-2.5 py-1 bg-warn-soft text-warn rounded text-xs">{dirty} unsaved change{dirty === 1 ? "" : "s"}</span>
      )}
      {!canSend && (
        <span className="px-2.5 py-1 bg-bad-soft text-bad rounded text-xs">Link {missing.join(" + ")} to send</span>
      )}
      <div className="flex-1" />
      <button type="button" onClick={onReject}  className="px-3 py-1.5 border border-bad text-bad rounded text-sm hover:bg-bad-soft">Reject</button>
      <button type="button" onClick={onSave} disabled={dirty === 0 || saving} className="px-3 py-1.5 border border-border rounded text-sm hover:bg-pill disabled:opacity-40">
        {saving ? "Saving…" : "Save draft"}
      </button>
      <button type="button" onClick={onHold}    className="px-3 py-1.5 border border-warn text-warn rounded text-sm bg-warn-soft hover:brightness-95">Hold</button>
      <button
        type="button"
        onClick={onImport}
        disabled={!canSend}
        title={sendTitle}
        className="px-3 py-1.5 bg-accent text-white rounded text-sm font-medium hover:brightness-95 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        Send to PK Bot ▸
      </button>
    </footer>
  );
}

// Builds the PATCH body from the dirty columns. Looks up each column in the
// RxDetail tree by snake_case → camelCase path.
function buildPatchBody(rx: RxDetail, dirty: Dirty): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const col of dirty) {
    out[col] = readColumn(rx, col);
  }
  return out;
}

// Hardcoded column → path map; mirrors the EDITABLE whitelist in rxUpdate.ts.
function readColumn(rx: RxDetail, col: string): unknown {
  switch (col) {
    case "patient_name_raw":     return rx.patient.nameRaw;
    case "patient_dob_raw":      return rx.patient.dobRaw;
    case "patient_phone_raw":    return rx.patient.phoneRaw;
    case "patient_address_raw":  return rx.patient.addressRaw;
    case "patient_species":      return rx.patient.species;
    case "patient_pet_name":     return rx.patient.petName;
    case "prescriber_name_raw":  return rx.prescriber.nameRaw;
    case "prescriber_npi_raw":   return rx.prescriber.npiRaw;
    case "prescriber_dea_raw":   return rx.prescriber.deaRaw;
    case "prescriber_phone_raw": return rx.prescriber.phoneRaw;
    case "prescriber_fax_raw":   return rx.prescriber.faxRaw;
    case "prescriber_clinic_raw":return rx.prescriber.clinicRaw;
    case "drug":                 return rx.prescription.drug;
    case "generic_name":         return rx.prescription.genericName;
    case "strength":             return rx.prescription.strength;
    case "form":                 return rx.prescription.form;
    case "sig":                  return rx.prescription.sig;
    case "quantity":             return rx.prescription.quantity;
    case "qty_on_refills":       return rx.prescription.qtyOnRefills;
    case "days_supply":          return rx.prescription.daysSupply;
    case "refills":              return rx.prescription.refills;
    case "refill_interval_days": return rx.prescription.refillIntervalDays;
    case "daw_code":             return rx.prescription.dawCode;
    case "rx_origin":            return rx.prescription.rxOrigin;
    case "date_written":         return rx.prescription.dateWritten;
    case "rx_expiration":        return rx.prescription.rxExpiration;
    case "counselling_received": return rx.prescription.counsellingReceived;
    case "rx_note":              return rx.prescription.rxNote;
    case "dispensing_note":      return rx.prescription.dispensingNote;
    case "billing_ndc":          return rx.prescription.billingNdc;
    case "shipping_method":      return rx.prescription.shippingMethod;
    case "compound_base":        return rx.compound.base;
    case "compound_notes":       return rx.compound.notes;
    default:                     return undefined;
  }
}

// Silence the unused-import warning for `useMemo` if not used in this version.
void useMemo;
